// Shared MFA error mapping for both the enrollment flow (settings page) and
// the sign-in flow (login page). Centralized so we never have to chase a
// "Failed to send code" / "Failed to verify" mystery again — the raw Firebase
// error code is always surfaced in the UI alongside a friendly message, the
// full error object is logged to the console, and a structured exception is
// shipped to PostHog Error Tracking.
//
// Added during #106 (May 2026).

import posthog from "posthog-js";

const MFA_ERROR_MESSAGES: Record<string, string> = {
  "auth/operation-not-allowed": "Phone-based 2FA is not enabled on this project. Contact an admin.",
  "auth/unverified-email": "Your email address must be verified before enabling 2FA.",
  "auth/requires-recent-login": "For security, please sign out and back in, then try again.",
  "auth/captcha-check-failed": "Captcha check failed. Refresh the page and try again.",
  "auth/invalid-app-credential": "App credential check failed. Likely a reCAPTCHA configuration issue — contact an admin.",
  "auth/missing-phone-number": "Please enter a phone number.",
  "auth/invalid-phone-number": "Invalid phone number. Include country code (e.g. +1 555 000 0000).",
  "auth/too-many-requests": "Too many attempts. Please wait a few minutes and try again.",
  "auth/quota-exceeded": "SMS quota reached. Please try again later.",
  "auth/invalid-verification-code": "Incorrect code. Please try again.",
  "auth/code-expired": "That code expired. Please request a new one.",
  "auth/missing-verification-code": "Please enter the 6-digit code.",
  "auth/missing-verification-id": "Verification session expired. Please start over.",
  "auth/second-factor-already-in-use": "A 2FA factor is already enrolled on this account.",
  "auth/maximum-second-factor-count-exceeded": "Maximum number of 2FA factors reached.",
  "auth/unsupported-first-factor": "Your sign-in method doesn't support adding 2FA. Use email + password instead.",
  "auth/missing-multi-factor-info": "Missing 2FA factor information.",
  "auth/missing-multi-factor-session": "2FA session expired. Please sign in again.",
  "auth/invalid-multi-factor-session": "Your 2FA session is invalid. Please sign in again.",
  "auth/multi-factor-info-not-found": "No matching 2FA factor found for your account.",
};

/**
 * Surface a Firebase MFA error in a user-readable way without losing the
 * underlying code. Always:
 *  - console.error()s the full error with the operation tag (good for support
 *    requests / PostHog correlation)
 *  - returns "{friendly message} ({code})" so the user can copy the raw code
 *    if they need to report it
 *
 * For codes not in the map (or non-Firebase errors that have no code at all),
 * returns an "Unexpected error" message that still includes whatever signal we
 * have — the error's name, message, and any code-like property — so the user
 * doesn't have to open DevTools to know what went wrong.
 */
export function friendlyMfaError(err: unknown, op: string): string {
  const errObj = err as { code?: string; message?: string; name?: string };
  const code = errObj?.code ?? "unknown";
  console.error(`[mfa:${op}] ${code}`, err);

  // Ship to PostHog Error Tracking so MFA failures are searchable + alertable.
  // Wraps non-Error throws (some Firebase paths) so captureException always
  // gets a real Error instance with a stack trace.
  if (typeof window !== "undefined") {
    const exception = err instanceof Error
      ? err
      : new Error(errObj?.message ?? `MFA ${op} failed (${code})`);
    posthog.captureException(exception, {
      $exception_source: "mfa-errors",
      mfa_operation: op,
      mfa_code: code,
      ...(errObj?.name ? { raw_error_name: errObj.name } : {}),
    });
  }

  const friendly = MFA_ERROR_MESSAGES[code];
  if (friendly) return `${friendly} (${code})`;

  // Unknown code path — surface as much as we can so the user can report it.
  // Order: friendly Firebase message (if any) > name + message > stringified > "unknown"
  const detailParts: string[] = [];
  if (code !== "unknown") detailParts.push(`code=${code}`);
  if (errObj?.name && errObj.name !== "Error") detailParts.push(`name=${errObj.name}`);
  if (errObj?.message) detailParts.push(`message=${truncate(errObj.message, 200)}`);
  const detail = detailParts.length > 0 ? detailParts.join(" | ") : safeStringify(err);
  return `Unexpected error during ${op}. ${detail}. Please share this with support.`;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}
